import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lightbulb, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";

export function WordSuggestions({ lang }) {
  const invalidate = useInvalidate();
  const { data: sugg = [] } = useQuery({ queryKey: ["word-suggestions", lang.id], queryFn: () => api.get(`/languages/${lang.id}/word-suggestions`).then((r) => r.data) });
  if (!sugg.length) return null;
  const refresh = () => invalidate(["word-suggestions", lang.id], ["words", lang.id], ["language", lang.id], ["languages", lang.universe_id]);
  const accept = async (s) => {
    try {
      await api.post(`/word-suggestions/${s.id}/accept`, { word: s.word, pronunciation: s.pronunciation, meaning: s.meaning, part_of_speech: s.part_of_speech, example: s.example, notes: s.notes });
      toast.success(`${s.word} added`);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const dismiss = async (s) => {
    await api.delete(`/word-suggestions/${s.id}`);
    refresh();
  };
  return (
    <div className="mb-8 rounded-md border border-[#14B8A6]/30 bg-[#14B8A6]/5 p-4" data-testid="word-suggestions">
      <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#14B8A6]"><Lightbulb className="h-4 w-4" />{sugg.length} words spotted in your notes</div>
      <div className="space-y-2">
        {sugg.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-3 rounded border border-[#2D3442] bg-[#0F131A] px-3 py-2" data-testid={`word-suggestion-${s.id}`}>
            <div className="min-w-0 text-sm">
              <span className="font-display text-lg text-[#F3EBDD]">{s.word}</span>
              {s.part_of_speech && <span className="ml-2 text-xs italic text-[#14B8A6]">{s.part_of_speech}</span>}
              <span className="ml-2 text-[#E8DFD1]">{s.meaning}</span>
              {s.source_name && <span className="ml-2 text-xs text-[#A8A090]">· from {s.source_name}</span>}
            </div>
            <div className="flex shrink-0 gap-1">
              <Button size="sm" onClick={() => accept(s)} data-testid={`word-suggestion-accept-${s.id}`}>Add</Button>
              <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => dismiss(s)} data-testid={`word-suggestion-dismiss-${s.id}`}><X className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
